import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Shield, Mail, Lock, User as UserIcon, AlertCircle, CheckCircle2, Info, Building2, Globe, ExternalLink } from 'lucide-react';
import axios from 'axios';
import ReCAPTCHA from 'react-google-recaptcha';
import CookieConsent from '@/components/CookieConsent';
import LegalModal from '@/components/LegalModal';

const PRIVACY_URL = 'https://www.beskarit.com/Privacy-Policy';
const TERMS_URL = 'https://www.beskarit.com/Terms-Of-Use';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// 2FA state
const TwoFactorVerification = ({ userId, deviceHash, onSuccess, onCancel }) => {
  const [otpCode, setOtpCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  
  const handleVerify = async () => {
    if (otpCode.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }
    
    setVerifying(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/verify-2fa`, {
        user_id: userId,
        otp_code: otpCode,
        device_hash: deviceHash
      });
      onSuccess(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setVerifying(false);
    }
  };
  
  return (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <div className="w-16 h-16 mx-auto bg-blue-100 rounded-full flex items-center justify-center mb-4">
          <Shield className="w-8 h-8 text-blue-600" />
        </div>
        <h2 className="text-xl font-semibold">Verify Your Identity</h2>
        <p className="text-gray-500 text-sm mt-2">
          We've sent a 6-digit code to your email
        </p>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="otp">Verification Code</Label>
        <Input
          id="otp"
          type="text"
          placeholder="Enter 6-digit code"
          value={otpCode}
          onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          className="text-center text-2xl tracking-widest"
          maxLength={6}
          autoFocus
        />
      </div>
      
      <div className="flex gap-3">
        <Button
          variant="outline"
          className="flex-1"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          className="flex-1"
          onClick={handleVerify}
          disabled={verifying || otpCode.length !== 6}
        >
          {verifying ? 'Verifying...' : 'Verify'}
        </Button>
      </div>
      
      <p className="text-xs text-center text-gray-500 mt-4">
        Code expires in 10 minutes. Check your spam folder if you don't see it.
      </p>
    </div>
  );
};

// Country flag colors for dynamic gradient
const COUNTRY_GRADIENTS = {
  US: { primary: '#002868', secondary: '#BF0A30', accent: '#FFFFFF' },
  GB: { primary: '#012169', secondary: '#C8102E', accent: '#FFFFFF' },
  CA: { primary: '#FF0000', secondary: '#FFFFFF', accent: '#FF0000' },
  AU: { primary: '#00008B', secondary: '#FF0000', accent: '#FFFFFF' },
  DE: { primary: '#000000', secondary: '#DD0000', accent: '#FFCE00' },
  FR: { primary: '#002395', secondary: '#ED2939', accent: '#FFFFFF' },
  IN: { primary: '#FF9933', secondary: '#138808', accent: '#FFFFFF' },
  JP: { primary: '#BC002D', secondary: '#FFFFFF', accent: '#BC002D' },
  BR: { primary: '#009739', secondary: '#FEDD00', accent: '#002776' },
  MX: { primary: '#006341', secondary: '#CE1126', accent: '#FFFFFF' },
  IT: { primary: '#008C45', secondary: '#CD212A', accent: '#FFFFFF' },
  ES: { primary: '#AA151B', secondary: '#F1BF00', accent: '#AA151B' },
  NL: { primary: '#21468B', secondary: '#AE1C28', accent: '#FFFFFF' },
  SE: { primary: '#006AA7', secondary: '#FECC00', accent: '#006AA7' },
  NO: { primary: '#BA0C2F', secondary: '#00205B', accent: '#FFFFFF' },
  DK: { primary: '#C8102E', secondary: '#FFFFFF', accent: '#C8102E' },
  FI: { primary: '#002F6C', secondary: '#FFFFFF', accent: '#002F6C' },
  PL: { primary: '#DC143C', secondary: '#FFFFFF', accent: '#DC143C' },
  RU: { primary: '#0039A6', secondary: '#D52B1E', accent: '#FFFFFF' },
  CN: { primary: '#DE2910', secondary: '#FFDE00', accent: '#DE2910' },
  KR: { primary: '#0047A0', secondary: '#C60C30', accent: '#FFFFFF' },
  SG: { primary: '#ED2939', secondary: '#FFFFFF', accent: '#ED2939' },
  AE: { primary: '#00732F', secondary: '#FF0000', accent: '#FFFFFF' },
  SA: { primary: '#006C35', secondary: '#FFFFFF', accent: '#006C35' },
  ZA: { primary: '#007749', secondary: '#FFB612', accent: '#000000' },
  DEFAULT: { primary: '#1e3a5f', secondary: '#3b82f6', accent: '#f59e0b' }
};

// Microsoft SVG icon
const MicrosoftIcon = () => (
  <svg className="w-5 h-5 mr-2" viewBox="0 0 21 21">
    <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
    <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
    <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
  </svg>
);

// Okta SVG icon
const OktaIcon = () => (
  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0C5.389 0 0 5.389 0 12s5.389 12 12 12 12-5.389 12-12S18.611 0 12 0zm0 18c-3.314 0-6-2.686-6-6s2.686-6 6-6 6 2.686 6 6-2.686 6-6 6z"/>
  </svg>
);

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [legalModalOpen, setLegalModalOpen] = useState(false);
  const [legalModalTab, setLegalModalTab] = useState('terms');
  const [countryCode, setCountryCode] = useState('DEFAULT');
  const [gradientColors, setGradientColors] = useState(COUNTRY_GRADIENTS.DEFAULT);
  const [show2FA, setShow2FA] = useState(false);
  const [twoFAData, setTwoFAData] = useState({ userId: null, deviceHash: null });
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirm_password: '',
    full_name: '',
    phone_number: '',
    phone_country_code: '+1',
    organization_name: '',
    country: '',
    terms_accepted: false
  });
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });
  const navigate = useNavigate();

  // Detect country from IP and set gradient colors
  useEffect(() => {
    const detectCountry = async () => {
      try {
        // Try to get country from backend which proxies the request
        const response = await axios.get(`${API_URL}/api/geo/detect`, { timeout: 3000 });
        const code = response.data?.country_code || 'DEFAULT';
        setCountryCode(code);
        setGradientColors(COUNTRY_GRADIENTS[code] || COUNTRY_GRADIENTS.DEFAULT);
      } catch (err) {
        // Fallback to default colors silently
        setGradientColors(COUNTRY_GRADIENTS.DEFAULT);
      }
    };
    detectCountry();
  }, []);

  // Ripple animation effect
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let ripples = [];
    let time = 0;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const createRipple = () => {
      const x = Math.random() * canvas.width;
      const y = Math.random() * canvas.height;
      ripples.push({
        x,
        y,
        radius: 0,
        maxRadius: 150 + Math.random() * 200,
        alpha: 0.4,
        color: Math.random() > 0.5 ? gradientColors.primary : gradientColors.secondary
      });
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw gradient background
      const gradient = ctx.createRadialGradient(
        canvas.width / 2, canvas.height / 2, 0,
        canvas.width / 2, canvas.height / 2, Math.max(canvas.width, canvas.height)
      );
      gradient.addColorStop(0, gradientColors.primary + '40');
      gradient.addColorStop(0.5, gradientColors.secondary + '30');
      gradient.addColorStop(1, gradientColors.primary + '20');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw ripples
      ripples.forEach((ripple, index) => {
        ripple.radius += 1.5;
        ripple.alpha -= 0.003;

        if (ripple.alpha <= 0 || ripple.radius >= ripple.maxRadius) {
          ripples.splice(index, 1);
          return;
        }

        ctx.beginPath();
        ctx.arc(ripple.x, ripple.y, ripple.radius, 0, Math.PI * 2);
        ctx.strokeStyle = ripple.color;
        ctx.globalAlpha = ripple.alpha;
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.globalAlpha = 1;
      });

      // Draw flowing waves
      time += 0.02;
      for (let i = 0; i < 3; i++) {
        ctx.beginPath();
        ctx.moveTo(0, canvas.height / 2);
        for (let x = 0; x < canvas.width; x += 5) {
          const y = canvas.height / 2 + 
            Math.sin(x * 0.01 + time + i * 0.5) * 50 +
            Math.sin(x * 0.02 + time * 1.5 + i) * 30;
          ctx.lineTo(x, y);
        }
        ctx.strokeStyle = i === 0 ? gradientColors.primary + '30' : 
                          i === 1 ? gradientColors.secondary + '20' : 
                          gradientColors.accent + '15';
        ctx.lineWidth = 3 - i * 0.5;
        ctx.stroke();
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    resize();
    window.addEventListener('resize', resize);
    animate();

    // Create ripples periodically
    const rippleInterval = setInterval(createRipple, 1500);

    return () => {
      window.removeEventListener('resize', resize);
      clearInterval(rippleInterval);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [gradientColors]);

  useEffect(() => {
    // Fetch countries list
    const fetchCountries = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/countries`);
        setCountries(response.data.countries || []);
      } catch (err) {
        console.error('Failed to fetch countries:', err);
        // Fallback countries
        setCountries([
          { code: 'US', name: 'United States' },
          { code: 'GB', name: 'United Kingdom' },
          { code: 'CA', name: 'Canada' },
          { code: 'AU', name: 'Australia' },
          { code: 'DE', name: 'Germany' },
          { code: 'FR', name: 'France' },
          { code: 'IN', name: 'India' },
          { code: 'JP', name: 'Japan' },
        ]);
      }
    };
    fetchCountries();
  }, []);

  const validatePassword = (password) => {
    setPasswordStrength({
      length: password.length >= 14,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password)
    });
  };

  const handlePasswordChange = (e) => {
    const password = e.target.value;
    setFormData({ ...formData, password });
    if (!isLogin) {
      validatePassword(password);
    }
  };

  const isPasswordValid = () => {
    return Object.values(passwordStrength).every(Boolean);
  };

  const passwordsMatch = () => {
    return formData.password === formData.confirm_password;
  };

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const response = await authAPI.login({
          email: formData.email,
          password: formData.password
        });
        
        // Check if 2FA is required (status 202 - success path in Axios)
        const data = response.data;
        if (response.status === 202 || data?.detail?.requires_2fa) {
          const detail = data.detail || data;
          setTwoFAData({
            userId: detail.user_id,
            deviceHash: detail.device_hash
          });
          setShow2FA(true);
          toast.info('Verification code sent to your email');
          setLoading(false);
          return;
        }
        
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('user', JSON.stringify(data.user));
        
        // Dispatch custom event to notify TenantContext
        window.dispatchEvent(new CustomEvent('user-logged-in'));
        
        toast.success('Login successful');
        navigate('/apps');
      } else {
        // Validate password
        if (!isPasswordValid()) {
          toast.error('Please ensure your password meets all requirements');
          setLoading(false);
          return;
        }
        
        // Validate passwords match
        if (!passwordsMatch()) {
          toast.error('Passwords do not match');
          setLoading(false);
          return;
        }
        
        // Validate terms accepted
        if (!formData.terms_accepted) {
          toast.error('Please accept the Terms of Use and Privacy Policy');
          setLoading(false);
          return;
        }
        
        // Validate country selected
        if (!formData.country) {
          toast.error('Please select your country');
          setLoading(false);
          return;
        }
        
        // Validate phone number
        if (!formData.phone_number || formData.phone_number.length < 7) {
          toast.error('Please enter a valid phone number');
          setLoading(false);
          return;
        }

        // Validate captcha (if enabled)
        // Note: In production, uncomment this check when CAPTCHA is configured
        // if (!captchaToken) {
        //   toast.error('Please complete the CAPTCHA verification');
        //   setLoading(false);
        //   return;
        // }

        await authAPI.register({
          email: formData.email,
          password: formData.password,
          confirm_password: formData.confirm_password,
          full_name: formData.full_name,
          phone_number: `${formData.phone_country_code}${formData.phone_number}`,
          organization_name: formData.organization_name || null,
          country: formData.country,
          terms_accepted: formData.terms_accepted,
          captcha_token: captchaToken
        });
        toast.success('Registration successful! Please login.');
        setIsLogin(true);
        setFormData({
          email: '',
          password: '',
          confirm_password: '',
          full_name: '',
          phone_number: '',
          phone_country_code: '+1',
          organization_name: '',
          country: '',
          terms_accepted: false
        });
        setPasswordStrength({
          length: false,
          uppercase: false,
          lowercase: false,
          number: false,
          special: false
        });
        setCaptchaToken(null);
      }
    } catch (error) {
      // Also handle 2FA in catch (some interceptors may reject non-200)
      if (error.response?.status === 202 || error.response?.data?.detail?.requires_2fa) {
        const detail = error.response.data?.detail || error.response.data;
        setTwoFAData({
          userId: detail.user_id,
          deviceHash: detail.device_hash
        });
        setShow2FA(true);
        toast.info('Verification code sent to your email');
      } else {
        const errorDetail = error.response?.data?.detail;
        const errorMessage = typeof errorDetail === 'string' ? errorDetail : 'Authentication failed';
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // SSO Handlers
  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/apps';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleMicrosoftLogin = () => {
    // Microsoft SSO - redirect to backend endpoint
    const redirectUrl = window.location.origin + '/apps';
    window.location.href = `${API_URL}/api/auth/sso/microsoft?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleOktaLogin = () => {
    // Okta SSO - redirect to backend endpoint
    const redirectUrl = window.location.origin + '/apps';
    window.location.href = `${API_URL}/api/auth/sso/okta?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const PasswordRequirement = ({ met, text }) => (
    <div className={`flex items-center gap-2 text-xs ${met ? 'text-green-600' : 'text-muted-foreground'}`}>
      {met ? (
        <CheckCircle2 className="w-3 h-3" />
      ) : (
        <AlertCircle className="w-3 h-3" />
      )}
      <span>{text}</span>
    </div>
  );

  const canSubmitRegistration = () => {
    return (
      formData.email &&
      formData.password &&
      formData.confirm_password &&
      formData.full_name &&
      formData.country &&
      formData.terms_accepted &&
      isPasswordValid() &&
      passwordsMatch()
    );
  };

  // Handlers for legal modal
  const openTermsModal = (e) => {
    e.preventDefault();
    setLegalModalTab('terms');
    setLegalModalOpen(true);
  };

  const openPrivacyModal = (e) => {
    e.preventDefault();
    setLegalModalTab('privacy');
    setLegalModalOpen(true);
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Animated background canvas */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 z-0"
        style={{ 
          background: `linear-gradient(135deg, ${gradientColors.primary}15 0%, ${gradientColors.secondary}10 50%, ${gradientColors.primary}20 100%)` 
        }}
      />

      <Card className="w-full max-w-md p-8 shadow-2xl relative z-20 backdrop-blur-sm bg-white/95 border-0" data-testid="auth-card">
        <div className="flex flex-col items-center mb-8">
          <div 
            className="p-4 rounded-2xl mb-4 transition-all duration-500"
            style={{ 
              background: `linear-gradient(135deg, ${gradientColors.primary}20, ${gradientColors.secondary}15)`,
              boxShadow: `0 8px 32px ${gradientColors.primary}30`
            }}
          >
            <img 
              src="/beskar-logo.png" 
              alt="Beskar Security Platform" 
              className="w-32 h-32 object-contain"
            />
          </div>
          <h1 
            className="text-3xl font-bold font-heading text-center"
            style={{ color: gradientColors.primary }}
          >
            Beskar Security Platform
          </h1>
          <p className="text-sm text-muted-foreground mt-2 text-center max-w-xs">
            Comprehensive Governance, Risk & Compliance Platform and Open Source Intelligence Analysis Platform
          </p>
        </div>

        {/* 2FA Verification Form */}
        {show2FA ? (
          <TwoFactorVerification
            userId={twoFAData.userId}
            deviceHash={twoFAData.deviceHash}
            onSuccess={(response) => {
              localStorage.setItem('token', response.access_token);
              localStorage.setItem('user', JSON.stringify(response.user));
              window.dispatchEvent(new CustomEvent('user-logged-in'));
              toast.success('Login successful');
              navigate('/apps');
            }}
            onCancel={() => {
              setShow2FA(false);
              setTwoFAData({ userId: null, deviceHash: null });
            }}
          />
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <>
              {/* Full Name */}
              <div className="space-y-2">
                <Label htmlFor="full_name">Full Name *</Label>
                <div className="relative">
                  <UserIcon className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="full_name"
                    type="text"
                    placeholder="John Doe"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="pl-10"
                    required
                    data-testid="full-name-input"
                  />
                </div>
              </div>

              {/* Organization Name */}
              <div className="space-y-2">
                <Label htmlFor="organization_name">Organization Name</Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="organization_name"
                    type="text"
                    placeholder="Acme Inc. (optional)"
                    value={formData.organization_name}
                    onChange={(e) => setFormData({ ...formData, organization_name: e.target.value })}
                    className="pl-10"
                    data-testid="organization-name-input"
                  />
                </div>
              </div>

              {/* Country */}
              <div className="space-y-2">
                <Label htmlFor="country">Country *</Label>
                <Select
                  value={formData.country}
                  onValueChange={(value) => {
                    setFormData({ ...formData, country: value });
                    // Update phone country code based on selected country
                    const countryCodes = {
                      US: '+1', CA: '+1', GB: '+44', AU: '+61', DE: '+49',
                      FR: '+33', IN: '+91', JP: '+81', BR: '+55', MX: '+52',
                      IT: '+39', ES: '+34', NL: '+31', SE: '+46', NO: '+47',
                      DK: '+45', FI: '+358', PL: '+48', RU: '+7', CN: '+86',
                      KR: '+82', SG: '+65', AE: '+971', SA: '+966', ZA: '+27'
                    };
                    if (countryCodes[value]) {
                      setFormData(prev => ({ ...prev, country: value, phone_country_code: countryCodes[value] }));
                    }
                  }}
                >
                  <SelectTrigger className="w-full" data-testid="country-select">
                    <Globe className="w-4 h-4 mr-2 text-muted-foreground" />
                    <SelectValue placeholder="Select your country" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {countries.map((country) => (
                      <SelectItem key={country.code} value={country.code}>
                        {country.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Phone Number */}
              <div className="space-y-2">
                <Label htmlFor="phone_number">Phone Number *</Label>
                <div className="flex gap-2">
                  <Select
                    value={formData.phone_country_code}
                    onValueChange={(value) => setFormData({ ...formData, phone_country_code: value })}
                  >
                    <SelectTrigger className="w-24">
                      <SelectValue placeholder="+1" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="+1">+1 (US/CA)</SelectItem>
                      <SelectItem value="+44">+44 (UK)</SelectItem>
                      <SelectItem value="+61">+61 (AU)</SelectItem>
                      <SelectItem value="+49">+49 (DE)</SelectItem>
                      <SelectItem value="+33">+33 (FR)</SelectItem>
                      <SelectItem value="+91">+91 (IN)</SelectItem>
                      <SelectItem value="+81">+81 (JP)</SelectItem>
                      <SelectItem value="+55">+55 (BR)</SelectItem>
                      <SelectItem value="+52">+52 (MX)</SelectItem>
                      <SelectItem value="+86">+86 (CN)</SelectItem>
                      <SelectItem value="+82">+82 (KR)</SelectItem>
                      <SelectItem value="+65">+65 (SG)</SelectItem>
                      <SelectItem value="+971">+971 (AE)</SelectItem>
                      <SelectItem value="+27">+27 (ZA)</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    id="phone_number"
                    type="tel"
                    placeholder="Phone number"
                    value={formData.phone_number}
                    onChange={(e) => {
                      // Only allow numbers
                      const value = e.target.value.replace(/\D/g, '');
                      setFormData({ ...formData, phone_number: value });
                    }}
                    className="flex-1"
                    required
                    data-testid="phone-input"
                  />
                </div>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Info className="w-3 h-3" />
                  Required for 2FA verification on new device login
                </p>
              </div>
            </>
          )}

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email">Email *</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="pl-10"
                required
                data-testid="email-input"
              />
            </div>
            {!isLogin && (
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Info className="w-3 h-3" />
                Public emails (Gmail, Yahoo, etc.) have limited OSINT access (5 searches/hour)
              </p>
            )}
          </div>

          {/* Password */}
          <div className="space-y-2">
            <Label htmlFor="password">Password *</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
              <Input
                id="password"
                type="password"
                placeholder={isLogin ? "••••••••" : "Minimum 14 characters"}
                value={formData.password}
                onChange={handlePasswordChange}
                className="pl-10"
                required
                data-testid="password-input"
              />
            </div>
            
            {/* Password requirements for registration */}
            {!isLogin && formData.password && (
              <div className="mt-2 p-3 bg-muted/50 rounded-md space-y-1">
                <p className="text-xs font-medium text-muted-foreground mb-2">Password Requirements:</p>
                <PasswordRequirement met={passwordStrength.length} text="At least 14 characters" />
                <PasswordRequirement met={passwordStrength.uppercase} text="One uppercase letter (A-Z)" />
                <PasswordRequirement met={passwordStrength.lowercase} text="One lowercase letter (a-z)" />
                <PasswordRequirement met={passwordStrength.number} text="One number (0-9)" />
                <PasswordRequirement met={passwordStrength.special} text="One special character (!@#$%^&*)" />
              </div>
            )}
          </div>

          {/* Confirm Password (registration only) */}
          {!isLogin && (
            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirm Password *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                <Input
                  id="confirm_password"
                  type="password"
                  placeholder="Re-enter your password"
                  value={formData.confirm_password}
                  onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                  className={`pl-10 ${
                    formData.confirm_password && !passwordsMatch() 
                      ? 'border-red-500 focus:ring-red-500' 
                      : ''
                  }`}
                  required
                  data-testid="confirm-password-input"
                />
              </div>
              {formData.confirm_password && !passwordsMatch() && (
                <p className="text-xs text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Passwords do not match
                </p>
              )}
              {formData.confirm_password && passwordsMatch() && (
                <p className="text-xs text-green-600 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  Passwords match
                </p>
              )}
            </div>
          )}

          {/* Terms & Privacy (registration only) */}
          {!isLogin && (
            <div className="space-y-3">
              {/* CAPTCHA placeholder - uncomment when configured */}
              {/* <div className="flex justify-center">
                <ReCAPTCHA
                  sitekey="YOUR_RECAPTCHA_SITE_KEY"
                  onChange={handleCaptchaChange}
                />
              </div> */}
              
              <div className="flex items-start gap-2">
                <Checkbox
                  id="terms"
                  checked={formData.terms_accepted}
                  onCheckedChange={(checked) => setFormData({ ...formData, terms_accepted: checked })}
                  data-testid="terms-checkbox"
                />
                <label htmlFor="terms" className="text-sm text-muted-foreground leading-tight cursor-pointer">
                  I agree to the{' '}
                  <a
                    href={TERMS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                    data-testid="signup-terms-link"
                  >
                    Terms of Use
                  </a>
                  {' '}and{' '}
                  <a
                    href={PRIVACY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                    data-testid="signup-privacy-link"
                  >
                    Privacy Policy
                  </a>
                </label>
              </div>
            </div>
          )}

          <Button
            type="submit"
            className="w-full"
            disabled={loading || (!isLogin && !canSubmitRegistration())}
            data-testid="submit-button"
          >
            {loading ? 'Processing...' : isLogin ? 'Sign In' : 'Create Account'}
          </Button>
        </form>
        )}

        {!show2FA && (
          <>
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border"></div>
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-white px-2 text-muted-foreground">Or continue with</span>
          </div>
        </div>

        {/* SSO Buttons */}
        <div className="space-y-3">
          {/* Google SSO */}
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={handleGoogleLogin}
            data-testid="google-login-button"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Sign in with Google
          </Button>

          {/* Microsoft SSO */}
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={handleMicrosoftLogin}
            data-testid="microsoft-login-button"
          >
            <MicrosoftIcon />
            Sign in with Microsoft
          </Button>

          {/* Okta SSO */}
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={handleOktaLogin}
            data-testid="okta-login-button"
          >
            <OktaIcon />
            Sign in with Okta
          </Button>
        </div>
          </>
        )}

        {!show2FA && (
        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => {
              setIsLogin(!isLogin);
              setFormData({
                email: '',
                password: '',
                confirm_password: '',
                full_name: '',
                phone_number: '',
                phone_country_code: '+1',
                organization_name: '',
                country: '',
                terms_accepted: false
              });
              setPasswordStrength({
                length: false,
                uppercase: false,
                lowercase: false,
                number: false,
                special: false
              });
              setCaptchaToken(null);
            }}
            className="text-sm text-primary hover:underline"
            data-testid="toggle-auth-mode"
          >
            {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
          </button>
        </div>
        )}

        <div className="mt-4 flex items-center justify-center gap-3 text-xs text-muted-foreground" data-testid="auth-legal-links">
          <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="hover:text-primary hover:underline"
            data-testid="auth-privacy-link">Privacy Policy</a>
          <span aria-hidden="true">·</span>
          <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className="hover:text-primary hover:underline"
            data-testid="auth-terms-link">Terms of Use</a>
        </div>
      </Card>

      {/* Cookie Consent Popup */}
      <CookieConsent />

      {/* Legal Modal for Terms & Privacy */}
      <LegalModal
        isOpen={legalModalOpen}
        onClose={() => setLegalModalOpen(false)}
        initialTab={legalModalTab}
      />
    </div>
  );
};

export default AuthPage;
